
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminSidebar from "@/components/admin/AdminSidebar";
import AdminHeader from "@/components/admin/AdminHeader";
import CivicEducationManagement from "@/components/admin/civic-education/CivicEducationManagement";
import { Button } from "@/components/ui/button";

const AdminCivicEducation = () => {
  const navigate = useNavigate();
  const { user, profile, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  
  useEffect(() => {
    document.title = "Civic Education - NationCard";
    
    // Only perform this check when auth loading is complete
    if (!isLoading) {
      if (!user) {
        toast.error("Please log in to access the admin dashboard");
        navigate("/admin");
        return;
      }
      
      if (!isAdmin() && !isSuperAdmin()) {
        toast.error("You do not have permission to access the admin dashboard");
        navigate("/");
        return;
      }
    }
  }, [user, profile, isAdmin, isSuperAdmin, navigate, isLoading]);
  
  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue mx-auto mb-4"></div>
        <p className="text-nation-blue">Loading admin dashboard...</p>
      </div>
    </div>;
  }
  
  // Additional check to ensure we have a user with proper permissions before rendering
  if (!user || (!isAdmin() && !isSuperAdmin())) {
    return null; // This will prevent render flashing while redirects happen
  }
  
  return (
    <div className="flex min-h-screen bg-nation-purple/10 dark:bg-gray-900/90">
      {/* Mobile sidebar toggle */}
      {isMobile && !sidebarOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-4 left-4 z-40 md:hidden"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-4 w-4" />
        </Button>
      )}
      
      {/* Sidebar with conditional display for mobile */}
      <div className={`${sidebarOpen ? "block" : "hidden"} ${isMobile ? "fixed inset-0 z-50 bg-black/50" : ""}`} onClick={isMobile ? () => setSidebarOpen(false) : undefined}>
        <div onClick={e => isMobile && e.stopPropagation()} className={isMobile ? "h-full w-64" : ""}>
          <AdminSidebar onClose={isMobile ? () => setSidebarOpen(false) : undefined} />
        </div>
      </div>
      
      <div className="flex-1">
        <AdminHeader>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          Civic Education Content
        </AdminHeader>
        
        <main className="p-4 md:p-6">
          <div className="mb-4">
            <div className="text-sm text-gray-500 mb-1">
              Admin / Civic Education
            </div>
          </div>
          <CivicEducationManagement />
        </main>
      </div>
    </div>
  );
};

export default AdminCivicEducation;
